/**
 * `canary workflow debug` — consolidated debug tool for a workflow run.
 *
 * Triangulates three signals so a human or AI agent can pinpoint what
 * happened in a run:
 *
 *   1. Visual    — Playwright trace (screenshots, video)
 *   2. DOM       — reconstructed snapshots of what the agent saw
 *   3. Telemetry — steps, cache, agent thoughts, Sentry logs/traces
 *
 * Subcommands are intentionally narrow so Claude/Codex can chain them:
 *   overview, steps, cache, thoughts, logs, trace, playwright, trace-snapshot,
 *   trace-fetch, snapshot, cache-debug, compare, bundle, variables
 *
 * Sentry-backed subcommands (`logs`, `trace`) need a `SENTRY_API_TOKEN`
 * configured on the API; without one they return an empty `partial: true`
 * payload with a reason rather than an error.
 */

import process from 'node:process';
import os from 'node:os';
import path from 'node:path';
import fs from 'node:fs/promises';
import { gunzipSync } from 'node:zlib';
import { apiRequest } from './cli-helpers.js';
import { hasFlag, getArgValue } from './auth.js';
import { runTrace } from './trace.js';
import {
  fetchWorkflowDebug as fetchDebug,
  fetchWorkflowRunDetail,
  lookupWorkflowIdForRun,
} from './workflow-debug/api-client.js';
import type {
  Envelope,
  RunDebugPayload,
  RunDebugStep,
  WorkflowDebugRunDetail,
} from './workflow-debug/types.js';
import type { RunData } from '@chatsdet/diagnostic-workspace';
import { command } from './brand.js';

function printDebugHelp(): void {
  console.log(
    [
      'Usage: canary workflow debug <sub-command> [options]',
      '',
      'Triangulate three signals to debug a workflow run:',
      '  1. Visual    — what happened on screen?  → debug playwright <runId>',
      '  2. DOM       — what did the agent see?   → debug trace-snapshot <runId>',
      '  3. Telemetry — what did we record?       → debug steps | cache | thoughts | logs',
      '',
      'Start with:   canary workflow debug <runId>    # diagnosis + pointers',
      'Then drill in with the subcommand matching your question:',
      '  "Where did we miss the cache?"   → debug cache --misses-only',
      '  "What steps were slow?"          → debug steps --slow',
      '  "Why did the agent click X?"     → debug thoughts --step <stepId>',
      '  "What was on the page?"          → debug trace-snapshot <runId>',
      '  "What errors did we log?"        → debug logs --level error',
      '  "What variables did we have?"    → debug variables <runId>',
      '  "What changed since it worked?"  → debug compare <oldRun> <newRun>',
      '  "Give me everything for a run"   → debug bundle <runId> --out <dir>',
      '',
      'Sub-commands:',
      '  overview <runId>                        Diagnosis summary (default when no sub given)',
      '  steps <runId> [--slow] [--json]         Step timings + status',
      '  cache <runId> [--misses-only] [--json]  Cache hit/miss breakdown',
      '  thoughts <runId> [--step <id>] [--grep <text>] [--json]',
      '                                           Agent reasoning for this run',
      '  navigation <runId> [--transitions-only] [--json]',
      '                                           Timestamped browser-URL history + cross-host transitions',
      '  logs <runId> [--step <id>] [--level info|warning|error] [--json]',
      '                                           Sentry logs for this run',
      '  trace <runId> [--step <id>] [--json]    Sentry span tree',
      '  playwright <runId> [--open] [--json]    Playwright trace viewer URL',
      '  trace-fetch <runId> [--out <path>] [--json]',
      '                                           Download trace.zip to disk',
      '  trace-snapshot <runId> [--action <index>] [--output-dir <path>] [--screenshots] [--html]',
      '                                           Download Playwright trace + reconstruct DOM snapshots',
      '  snapshot <runId> [--out <path>] [--json] Workflow definition that ran (published or draft)',
      '  cache-debug <runId> [--step <id>] [--json]',
      '                                           Per-step cache compilation + replay details',
      '  variables <runId> [--name <var>] [--json]',
      '                                           Run-input + captured variables with definition timestamps',
      '  compare <runIdA> <runIdB> [--out <dir>] [--json]',
      '                                           Diff two runs (steps, thoughts, cache, snapshot)',
      '  bundle <runId> --out <dir>               Pull all debug artifacts into one directory',
      '  materialize <runId> [--out <dir>] [--json]',
      '                                           Materialize the canonical diagnostic workspace (same shape',
      '                                           the v2 failure-diagnostic agent uses). Start with `summary.md`.',
      '  sandbox-logs <runId> [--out <path>] [--json]',
      '                                           Read retained application logs from the target sandbox.',
      '',
      'Options:',
      '  --workflow <id>    Workflow ID (auto-resolved from runId if omitted)',
      '  --json             Raw JSON output',
      '  --env <env>        Target environment (prod, dev, local)',
    ].join('\n')
  );
}

export async function handleWorkflowDebug(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const [maybeSub, ...rest] = argv;

  if (!maybeSub || maybeSub === 'help' || hasFlag(argv, '--help', '-h')) {
    printDebugHelp();
    return;
  }

  // Bare form: `canary workflow debug <runId>` → overview
  if (looksLikeRunId(maybeSub)) {
    await handleOverview([maybeSub, ...rest], apiUrl, token);
    return;
  }

  switch (maybeSub) {
    case 'overview':
      await handleOverview(rest, apiUrl, token);
      return;
    case 'steps':
      await handleSteps(rest, apiUrl, token);
      return;
    case 'cache':
      await handleCache(rest, apiUrl, token);
      return;
    case 'thoughts':
      await handleThoughts(rest, apiUrl, token);
      return;
    case 'navigation':
      await handleNavigation(rest, apiUrl, token);
      return;
    case 'logs':
      await handleLogs(rest, apiUrl, token);
      return;
    case 'trace':
      await handleSentryTrace(rest, apiUrl, token);
      return;
    case 'playwright':
      await handlePlaywright(rest, apiUrl, token);
      return;
    case 'trace-snapshot':
      await handleTraceSnapshot(rest, apiUrl, token);
      return;
    case 'trace-fetch':
      await handleTraceFetch(rest, apiUrl, token);
      return;
    case 'snapshot':
      await handleSnapshot(rest, apiUrl, token);
      return;
    case 'cache-debug':
      await handleCacheDebug(rest, apiUrl, token);
      return;
    case 'variables':
      await handleVariables(rest, apiUrl, token);
      return;
    case 'compare':
      await handleCompare(rest, apiUrl, token);
      return;
    case 'bundle':
      await handleBundle(rest, apiUrl, token);
      return;
    case 'materialize':
      await handleMaterialize(rest, apiUrl, token);
      return;
    case 'sandbox-logs':
      await handleSandboxLogs(rest, apiUrl, token);
      return;
    default:
      console.error(`Unknown debug sub-command: ${maybeSub}`);
      printDebugHelp();
      process.exit(1);
  }
}

function looksLikeRunId(s: string): boolean {
  // Uuid-ish or prefixed id. Just avoid matching bare sub-commands.
  return !!s && !s.startsWith('-') && s.length >= 8 && !/^[a-z-]+$/i.test(s.slice(0, 8));
}

function requireRunId(argv: string[]): string {
  const runId = argv.find((a) => !a.startsWith('-'));
  if (!runId) {
    console.error('Error: <runId> is required');
    process.exit(1);
  }
  return runId;
}

async function resolveWorkflowId(
  argv: string[],
  runId: string,
  apiUrl: string,
  token: string
): Promise<string> {
  const explicit = getArgValue(argv, '--workflow');
  try {
    return await resolveWorkflowIdForRun({ runId, explicitWorkflowId: explicit, apiUrl, token });
  } catch (error) {
    console.error(error instanceof Error ? error.message : String(error));
    console.error(
      `       Pass --workflow <workflowId> explicitly, or run: canary workflow runs list <workflowId>`
    );
    process.exit(1);
  }
}

export async function resolveWorkflowIdForRun(opts: {
  runId: string;
  explicitWorkflowId?: string;
  apiUrl: string;
  token: string;
}): Promise<string> {
  if (opts.explicitWorkflowId) return opts.explicitWorkflowId;
  const res = await lookupWorkflowIdForRun(opts.apiUrl, opts.token, opts.runId);
  if (res.ok && res.data?.workflowId) return res.data.workflowId;
  throw new Error(
    `Error: could not resolve workflowId from runId (${res.error ?? 'unknown error'}).`
  );
}

function fmtMs(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function formatRetention(expiresAt: string | null, nowMs = Date.now()): string {
  if (!expiresAt) return 'unknown expiry';
  const remainingMs = new Date(expiresAt).getTime() - nowMs;
  if (remainingMs <= 0) return 'expired';
  const minutes = Math.ceil(remainingMs / 60_000);
  if (minutes < 60) return `${minutes}m remaining`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${hours}h${remainder > 0 ? ` ${remainder}m` : ''} remaining`;
}

async function handleOverview(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = (await fetchDebug(apiUrl, token, workflowId, runId)) as Envelope<RunDebugPayload>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }
  const d = res.data;
  console.log(`Run ${d.run.id.slice(0, 8)}  ·  ${d.run.status}  ·  ${fmtMs(d.run.durationMs)}`);
  if (d.run.errorMessage) console.log(`  error: ${d.run.errorMessage}`);
  console.log(
    `  cache: ${Math.round(d.cache.hitRate * 100)}% hit (${d.cache.hitCount}/${d.cache.hitCount + d.cache.missCount})`
  );
  console.log(`  thoughts: ${d.thoughts.count} recorded`);
  console.log(`  playwright trace: ${d.playwright.traceStorageKey ? 'available' : 'not captured'}`);

  if (d.diagnosis.failedSteps.length > 0) {
    console.log('\nFailed steps:');
    for (const s of d.diagnosis.failedSteps) {
      console.log(`  ✗ ${s.name ?? s.stepId.slice(0, 8)}  ${s.errorMessage ?? ''}`);
    }
  }
  if (d.diagnosis.slowestSteps.length > 0) {
    console.log('\nSlowest steps:');
    for (const s of d.diagnosis.slowestSteps) {
      console.log(`  · ${s.name ?? s.stepId.slice(0, 8)}  ${fmtMs(s.durationMs)}`);
    }
  }
  if (d.diagnosis.cacheMissHotspots.length > 0) {
    console.log('\nCache miss hotspots:');
    for (const s of d.diagnosis.cacheMissHotspots) {
      console.log(`  · ${s.stepId.slice(0, 8)}  ${s.reason}`);
    }
  }

  if (d.repro?.sandbox) {
    console.log('\nManual repro:');
    console.log(
      `  sandbox: ${d.repro.available ? d.repro.sandbox.publicUrl : `unavailable (${d.repro.unavailableReason ?? d.repro.sandbox.status})`}`
    );
    console.log(`  retention: ${formatRetention(d.repro.sandbox.expiresAt)}`);
    if (d.repro.available && d.repro.credentials.length > 0) {
      const targetEnv = getArgValue(argv, '--env') ?? 'prod';
      console.log('  credentials:');
      for (const credential of d.repro.credentials) {
        const role = credential.role ? ` (${credential.role})` : '';
        console.log(`    ${credential.name}${role}  ${credential.id}`);
        console.log(`      cofactor credential get ${credential.id} --env ${targetEnv}`);
      }
    } else if (d.repro.available) {
      console.log('  credentials: none discovered for this sandbox');
    }
  }

  if (d.execution) {
    console.log('\nExecution:');
    if (d.execution.targetEnvironment) {
      console.log(
        `  target: ${d.execution.targetEnvironment.name} (${d.execution.targetEnvironment.type})  ${d.execution.targetEnvironment.id}`
      );
    }
    if (d.execution.runtimeEnvironment) {
      console.log(
        `  runtime: ${d.execution.runtimeEnvironment.name} (${d.execution.runtimeEnvironment.type})  ${d.execution.runtimeEnvironment.id}`
      );
    }
    if (d.execution.sandbox) {
      console.log(
        `  sandbox: ${d.execution.sandbox.name}  ${d.execution.sandbox.id}  (${d.execution.sandbox.status})`
      );
      console.log(
        `  sandbox logs: ${d.sandboxLogs?.status ?? 'not captured'}${
          d.sandboxLogs ? ` · ${d.sandboxLogs.eventCount} events` : ''
        }`
      );
      if (d.sandboxLogs?.applicationEventCount !== undefined) {
        const appStreamCount = Object.keys(d.sandboxLogs.streamEventCounts ?? {}).filter((stream) =>
          stream.startsWith('sandbox/app/')
        ).length;
        console.log(
          `  application logs: ${d.sandboxLogs.applicationEventCount} events · ${appStreamCount} service stream${appStreamCount === 1 ? '' : 's'}`
        );
      }
    }
  }

  console.log('\nNext:');
  console.log(`  canary workflow debug steps ${runId} --slow          # timings`);
  console.log(`  canary workflow debug cache ${runId} --misses-only   # cache diagnosis`);
  console.log(`  canary workflow debug thoughts ${runId} --step <id>  # agent reasoning`);
  console.log(`  canary workflow debug trace-snapshot ${runId}        # DOM the agent saw`);
  if (d.execution?.sandbox) {
    if (d.execution.sandbox.liveUrl) {
      console.log(
        `  cofactor sandbox logs ${d.execution.sandbox.id}                 # live sandbox logs`
      );
    }
    console.log(`  cofactor workflow debug sandbox-logs ${runId}       # retained run window`);
  }
}

async function handleSteps(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = (await fetchDebug(apiUrl, token, workflowId, runId, '/steps')) as Envelope<{
    steps: RunDebugStep[];
  }>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  const steps = [...res.data.steps];
  if (hasFlag(argv, '--slow')) {
    steps.sort((a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0));
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(steps, null, 2));
    return;
  }
  console.log('STEP                         STATUS     DURATION   CACHE');
  for (const s of steps) {
    const name = (s.name ?? s.id.slice(0, 8)).padEnd(28).slice(0, 28);
    const status = s.status.padEnd(10);
    const dur = fmtMs(s.durationMs).padEnd(10);
    const cache = s.cacheHitSource ?? s.cacheOutcome ?? '—';
    console.log(`${name} ${status} ${dur} ${cache}`);
    if (s.errorMessage) console.log(`  ↳ ${s.errorMessage}`);
  }
}

type NavigationEntry = {
  timestampMs: number;
  isoTimestamp: string | null;
  url: string;
  hostname: string | null;
  success: boolean;
};

type NavigationTransition = {
  fromIndex: number;
  toIndex: number;
  fromHostname: string | null;
  toHostname: string | null;
  fromUrl: string;
  toUrl: string;
  timestampMs: number;
};

type NavigationPayload = {
  startTimestamp: string | null;
  entries: NavigationEntry[];
  transitions: NavigationTransition[];
};

async function handleNavigation(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = (await fetchDebug(
    apiUrl,
    token,
    workflowId,
    runId,
    '/navigation'
  )) as Envelope<NavigationPayload>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }

  const transitionsOnly = hasFlag(argv, '--transitions-only');
  const payload = res.data;

  if (hasFlag(argv, '--json')) {
    console.log(
      JSON.stringify(
        transitionsOnly
          ? { startTimestamp: payload.startTimestamp, transitions: payload.transitions }
          : payload,
        null,
        2
      )
    );
    return;
  }

  if (payload.entries.length === 0) {
    console.log('No navigation events recorded for this run.');
    return;
  }

  if (payload.transitions.length > 0) {
    console.log(
      `⚠  ${payload.transitions.length} cross-host transition${payload.transitions.length === 1 ? '' : 's'}:`
    );
    for (const t of payload.transitions) {
      console.log(`   ${formatRelMs(t.timestampMs)}  ${t.fromHostname} → ${t.toHostname}`);
    }
    console.log('');
  }

  if (transitionsOnly) return;

  const transitionTo = new Set(payload.transitions.map((t) => t.toIndex));
  console.log('TIME         HOSTNAME                  URL');
  payload.entries.forEach((e, idx) => {
    const time = formatRelMs(e.timestampMs).padEnd(12);
    const host = (e.hostname ?? '—').padEnd(25).slice(0, 25);
    const marker = transitionTo.has(idx) ? '→' : ' ';
    console.log(`${time} ${marker} ${host} ${e.url}`);
  });
}

function formatRelMs(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const millis = ms % 1000;
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

async function handleCache(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = (await fetchDebug(apiUrl, token, workflowId, runId)) as Envelope<RunDebugPayload>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  const { cache, steps } = res.data;
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(cache, null, 2));
    return;
  }
  console.log(
    `Cache hit rate: ${Math.round(cache.hitRate * 100)}%  (${cache.hitCount} hit, ${cache.missCount} miss)`
  );
  if (Object.keys(cache.bySource).length > 0) {
    console.log('By source:');
    for (const [src, n] of Object.entries(cache.bySource)) {
      console.log(`  ${src.padEnd(16)} ${n}`);
    }
  }
  const stepById = new Map(steps.map((s) => [s.id, s]));
  if (!hasFlag(argv, '--misses-only')) {
    console.log('\nHits:');
    for (const s of steps.filter((x) => x.cacheHitSource)) {
      console.log(`  ✓ ${(s.name ?? s.id.slice(0, 8)).padEnd(28)}  ${s.cacheHitSource}`);
    }
  }
  if (cache.misses.length > 0) {
    console.log('\nMisses:');
    for (const m of cache.misses) {
      const s = stepById.get(m.stepId);
      console.log(`  ✗ ${(s?.name ?? m.stepId.slice(0, 8)).padEnd(28)}  ${m.reason}`);
    }
  }
}

async function handleThoughts(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const stepId = getArgValue(argv, '--step');
  const grep = getArgValue(argv, '--grep');
  const qs = stepId ? `?step=${encodeURIComponent(stepId)}` : '';
  const res = (await fetchDebug(apiUrl, token, workflowId, runId, `/thoughts${qs}`)) as Envelope<{
    thoughts: Array<{
      id: string;
      stepId: string | null;
      toolName: string | null;
      content: string;
      orderIndex: number;
      createdAt: string;
    }>;
  }>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  const totalThoughts = res.data.thoughts.length;
  let thoughts = res.data.thoughts;
  if (grep) {
    const needle = grep.toLowerCase();
    thoughts = thoughts.filter((t) => t.content.toLowerCase().includes(needle));
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(thoughts, null, 2));
    return;
  }
  for (const t of thoughts) {
    const tag = t.toolName ? `[${t.toolName}] ` : '';
    const step = t.stepId ? `step ${t.stepId.slice(0, 8)}  ` : '';
    console.log(`${step}${tag}${t.content}`);
    console.log('');
  }
  if (thoughts.length === 0) {
    if (totalThoughts === 0) {
      const scope = stepId ? ` for step ${stepId.slice(0, 8)}` : '';
      console.log(`(no thoughts recorded${scope})`);
    } else if (grep) {
      console.log(
        `(no thoughts matched --grep "${grep}"; ${totalThoughts} total thought${totalThoughts === 1 ? '' : 's'} in scope)`
      );
    } else {
      console.log('(no thoughts to display)');
    }
  }
}

async function handleLogs(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const level = getArgValue(argv, '--level');
  const stepId = getArgValue(argv, '--step');
  const params = new URLSearchParams();
  if (level) params.set('level', level);
  if (stepId) params.set('step', stepId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = (await fetchDebug(apiUrl, token, workflowId, runId, `/logs${qs}`)) as Envelope<{
    rows: Array<{ timestamp: string; level: string; message: string }>;
    partial: boolean;
    reason: string | null;
  }>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }
  if (res.data.partial) {
    console.log(`(partial result: ${res.data.reason ?? 'unknown'})`);
  }
  for (const row of res.data.rows) {
    console.log(`${row.timestamp}  ${row.level.padEnd(8)}  ${row.message}`);
  }
  if (res.data.rows.length === 0 && !res.data.partial) {
    console.log('(no logs)');
  }
}

async function handleSentryTrace(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const stepId = getArgValue(argv, '--step');
  const qs = stepId ? `?step=${encodeURIComponent(stepId)}` : '';
  const res = (await fetchDebug(
    apiUrl,
    token,
    workflowId,
    runId,
    `/sentry-trace${qs}`
  )) as Envelope<{
    traceId: string | null;
    spans: Array<{
      spanId: string;
      parentSpanId: string | null;
      op: string | null;
      description: string | null;
      durationMs: number | null;
    }>;
    partial: boolean;
    reason: string | null;
  }>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }
  if (res.data.partial) {
    console.log(`(partial result: ${res.data.reason ?? 'unknown'})`);
  }
  if (res.data.traceId) console.log(`trace ${res.data.traceId}`);
  for (const span of res.data.spans) {
    const op = (span.op ?? 'span').padEnd(22);
    const desc = (span.description ?? '').padEnd(40);
    const dur = span.durationMs !== null ? `${span.durationMs}ms` : '';
    console.log(`  ${op} ${desc} ${dur}`);
  }
  if (res.data.spans.length === 0 && !res.data.partial) {
    console.log('(no spans)');
  }
}

async function handlePlaywright(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = (await fetchDebug(apiUrl, token, workflowId, runId, '/trace')) as Envelope<{
    viewerUrl: string;
    signedUrl: string;
    storageKey: string;
  }>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(res.data, null, 2));
    return;
  }
  console.log(res.data.viewerUrl);
  if (hasFlag(argv, '--open')) {
    const opener =
      process.platform === 'darwin' ? 'open' : process.platform === 'win32' ? 'start' : 'xdg-open';
    const { spawn } = await import('node:child_process');
    spawn(opener, [res.data.viewerUrl], { detached: true, stdio: 'ignore' }).unref();
  }
}

async function handleTraceSnapshot(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  if (getArgValue(argv, '--step')) {
    console.error(
      'Error: trace-snapshot --step is not supported by the local trace reconstructor. Use --action <0-based trace action index> or omit filtering.'
    );
    process.exit(1);
  }
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = (await fetchDebug(apiUrl, token, workflowId, runId, '/trace')) as Envelope<{
    signedUrl: string;
    storageKey: string;
  }>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }

  // Download into a stable temp path so `canary trace snapshot` can open it.
  const tmp = await fs.mkdtemp(path.join(os.tmpdir(), `canary-trace-${runId}-`));
  const traceFile = path.join(tmp, 'trace.zip');
  const download = await fetch(res.data.signedUrl);
  if (!download.ok) {
    console.error(`Failed to download trace: HTTP ${download.status}`);
    process.exit(1);
  }
  const buf = Buffer.from(await download.arrayBuffer());
  await fs.writeFile(traceFile, buf);

  const forwarded: string[] = ['snapshot', traceFile];
  for (const flag of ['--output-dir', '--filter', '--action']) {
    const v = getArgValue(argv, flag);
    if (v !== undefined) forwarded.push(flag, v);
  }
  if (hasFlag(argv, '--screenshots')) forwarded.push('--screenshots');
  if (hasFlag(argv, '--html')) forwarded.push('--html');

  await runTrace(forwarded);
}

/**
 * `trace-fetch` — download the raw `trace.zip` to a chosen path.
 *
 * Distinct from `playwright` (prints/opens viewer URL) and `trace-snapshot`
 * (downloads to a tmp dir and pipes into `canary trace snapshot`). Use this
 * when you just want the file on disk for `unzip`, vendoring into a ticket,
 * or feeding to another tool.
 */
async function handleTraceFetch(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = (await fetchDebug(apiUrl, token, workflowId, runId, '/trace')) as Envelope<{
    signedUrl: string;
    storageKey: string;
  }>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  const out = getArgValue(argv, '--out') ?? `./trace-${runId}.zip`;
  const download = await fetch(res.data.signedUrl);
  if (!download.ok) {
    console.error(`Failed to download trace: HTTP ${download.status}`);
    process.exit(1);
  }
  const buf = Buffer.from(await download.arrayBuffer());
  await fs.writeFile(out, buf);
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify({ path: out, storageKey: res.data.storageKey }, null, 2));
    return;
  }
  console.log(out);
}

async function fetchRunDetail(
  apiUrl: string,
  token: string,
  workflowId: string,
  runId: string
): Promise<WorkflowDebugRunDetail> {
  const { run, error } = await fetchWorkflowRunDetail(apiUrl, token, workflowId, runId);
  if (!run) {
    console.error(`Failed to load run: ${error ?? 'unknown error'}`);
    process.exit(1);
  }
  return run;
}

/**
 * `snapshot` — fetch the workflow definition that ran for this runId.
 *
 * Resolves to either a published snapshot (`snapshotVersion`) or a draft
 * snapshot (`draftSnapshotId.version`) by looking at the run record, then
 * fetches the corresponding `snapshotJson`. Useful for "what was the workflow
 * when this ran?" and as the input to `diff` for two runs.
 */
async function handleSnapshot(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const run = await fetchRunDetail(apiUrl, token, workflowId, runId);
  if (!run) return;

  let snapshotPath: string;
  let kind: 'published' | 'draft';
  if (run.draftSnapshot) {
    snapshotPath = `/workflows/${encodeURIComponent(workflowId)}/draft-snapshots/${encodeURIComponent(run.draftSnapshot.version)}`;
    kind = 'draft';
  } else if (run.snapshotVersion !== null && run.snapshotVersion !== undefined) {
    snapshotPath = `/workflows/${encodeURIComponent(workflowId)}/snapshots/${run.snapshotVersion}`;
    kind = 'published';
  } else {
    console.error('Run has no snapshotVersion or draftSnapshotId — cannot resolve definition.');
    process.exit(1);
  }

  const snap = await apiRequest<{
    ok: boolean;
    error?: string;
    snapshot?: { snapshotJson: unknown; version: number | string; name?: string | null };
  }>(apiUrl, token, 'GET', snapshotPath);
  if (!snap.snapshot) {
    console.error(`Failed to load snapshot: ${snap.error ?? 'unknown error'}`);
    process.exit(1);
  }

  const out = getArgValue(argv, '--out');
  const text = JSON.stringify(snap.snapshot.snapshotJson, null, 2);
  if (out) {
    await fs.writeFile(out, text);
    if (hasFlag(argv, '--json')) {
      console.log(JSON.stringify({ path: out, kind, version: snap.snapshot.version }, null, 2));
    } else {
      console.log(`${kind} snapshot v${snap.snapshot.version} → ${out}`);
    }
    return;
  }
  if (hasFlag(argv, '--json')) {
    console.log(
      JSON.stringify(
        { kind, version: snap.snapshot.version, snapshotJson: snap.snapshot.snapshotJson },
        null,
        2
      )
    );
    return;
  }
  console.log(text);
}

type VariablesPayload = {
  inputs: Array<{
    name: string;
    value: string;
    definedAt: string;
    source: 'run-input';
  }>;
  captures: Array<{
    name: string;
    value: string;
    definedAt: string;
    source: 'capture';
    stepId: string;
    nodeId: string;
    nodeName: string | null;
  }>;
};

/**
 * `variables` — list run-time variables and where each one was defined.
 *
 * Two sections: workflow-level inputs (defined at run start) and per-step
 * captures (defined when the agent's saveAs directive fires). Use this to
 * answer "what was {{var}} when this ran, and which step set it?"
 */
async function handleVariables(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = (await fetchDebug(
    apiUrl,
    token,
    workflowId,
    runId,
    '/variables'
  )) as Envelope<VariablesPayload>;
  if (!res.ok) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  const filter = getArgValue(argv, '--name');
  const data: VariablesPayload = filter
    ? {
        inputs: res.data.inputs.filter((v) => v.name === filter),
        captures: res.data.captures.filter((v) => v.name === filter),
      }
    : res.data;

  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const fmtValue = (v: string) => (v.length > 80 ? `${v.slice(0, 77)}...` : v);

  console.log(`Run inputs (${data.inputs.length})`);
  if (data.inputs.length === 0) {
    console.log('  (none)');
  } else {
    for (const v of data.inputs) {
      console.log(`  ${v.name} = ${fmtValue(v.value)}    [defined ${v.definedAt}]`);
    }
  }

  console.log('');
  console.log(`Captures (${data.captures.length})`);
  if (data.captures.length === 0) {
    console.log('  (none)');
  } else {
    for (const v of data.captures) {
      const where = v.nodeName ? `${v.nodeName} (${v.stepId})` : v.stepId;
      console.log(`  ${v.name} = ${fmtValue(v.value)}    [${v.definedAt} · ${where}]`);
    }
  }
}

type CacheDebugResponse = {
  ok?: boolean;
  error?: string;
  cache?: unknown;
  steps?: Array<Record<string, unknown>>;
  debugSnapshots?: unknown[];
};
type CacheDebugEnvelope = CacheDebugResponse & { ok: boolean; data?: CacheDebugResponse };

/**
 * `cache-debug` — wrap the cache-debug endpoint.
 *
 * Exposes per-step cache compilation, attempted selectors, resolution results,
 * and a11y snapshots from `workflow_cache_debug_snapshots`. This is the data
 * that powers the cache outcomes tab in the web debug view.
 */
async function handleCacheDebug(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const res = await apiRequest<CacheDebugEnvelope>(
    apiUrl,
    token,
    'GET',
    `/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(runId)}/cache-debug`
  );
  if (res.ok === false) {
    console.error(`Failed: ${res.error ?? 'unknown error'}`);
    process.exit(1);
  }
  const stepFilter = getArgValue(argv, '--step');
  const payload = res.data ?? res;
  const data: CacheDebugResponse = stepFilter
    ? {
        ...payload,
        steps: (payload.steps ?? []).filter(
          (s) => s['stepId'] === stepFilter || s['nodeId'] === stepFilter
        ),
      }
    : payload;
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  console.log(JSON.stringify(data, null, 2));
}

type StepLite = {
  id: string;
  nodeId: string;
  name: string | null;
  status: string;
  durationMs: number | null;
  cacheHitSource: string | null;
  cacheOutcome: string | null;
  errorMessage: string | null;
};
type ThoughtLite = {
  id: string;
  stepId: string | null;
  content: string;
  toolName: string | null;
  orderIndex: number;
};
type RunBundle = {
  runId: string;
  overview: RunDebugPayload;
  steps: StepLite[];
  thoughts: ThoughtLite[];
};

async function loadRunBundle(
  apiUrl: string,
  token: string,
  workflowId: string,
  runId: string
): Promise<RunBundle> {
  const overview = (await fetchDebug(
    apiUrl,
    token,
    workflowId,
    runId
  )) as Envelope<RunDebugPayload>;
  if (!overview.ok) {
    console.error(`Failed to load run ${runId}: ${overview.error ?? 'unknown error'}`);
    process.exit(1);
  }
  const stepsRes = (await fetchDebug(apiUrl, token, workflowId, runId, '/steps')) as Envelope<{
    steps: StepLite[];
  }>;
  const thoughtsRes = (await fetchDebug(
    apiUrl,
    token,
    workflowId,
    runId,
    '/thoughts'
  )) as Envelope<{ thoughts: ThoughtLite[] }>;
  return {
    runId,
    overview: overview.data,
    steps: stepsRes.ok ? stepsRes.data.steps : [],
    thoughts: thoughtsRes.ok ? thoughtsRes.data.thoughts : [],
  };
}

/**
 * `compare` — structural diff between two runs.
 *
 * Aligns steps by nodeId; reports status/duration/cacheHitSource flips and
 * thought additions at diverging steps. Output is a JSON document the skill
 * can read and reason over (no LLM summarization in the CLI itself). With
 * `--out`, also pulls `trace.zip` and the snapshot JSON for both runs into
 * `<dir>/runA/` and `<dir>/runB/` for offline visual inspection.
 */
async function handleCompare(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  if (positional.length < 2) {
    console.error(
      'Usage: canary workflow debug compare <runIdA> <runIdB> [--workflow <id>] [--out <dir>] [--json]'
    );
    process.exit(1);
  }
  const [runA, runB] = positional;
  const workflowId = await resolveWorkflowId(argv, runA, apiUrl, token);

  const [a, b] = await Promise.all([
    loadRunBundle(apiUrl, token, workflowId, runA),
    loadRunBundle(apiUrl, token, workflowId, runB),
  ]);

  const stepsByNode = (steps: StepLite[]) => {
    const m = new Map<string, StepLite>();
    for (const s of steps) m.set(s.nodeId, s);
    return m;
  };
  const aMap = stepsByNode(a.steps);
  const bMap = stepsByNode(b.steps);
  const allNodes = new Set([...aMap.keys(), ...bMap.keys()]);

  const stepDiffs: Array<{
    nodeId: string;
    name: string | null;
    statusA: string | null;
    statusB: string | null;
    durationA: number | null;
    durationB: number | null;
    durationDeltaPct: number | null;
    cacheA: string | null;
    cacheB: string | null;
    flipped: string[];
  }> = [];

  for (const nodeId of allNodes) {
    const sa = aMap.get(nodeId) ?? null;
    const sb = bMap.get(nodeId) ?? null;
    const flipped: string[] = [];
    if ((sa?.status ?? null) !== (sb?.status ?? null)) flipped.push('status');
    if ((sa?.cacheHitSource ?? null) !== (sb?.cacheHitSource ?? null)) flipped.push('cache');
    const durA = sa?.durationMs ?? null;
    const durB = sb?.durationMs ?? null;
    let durationDeltaPct: number | null = null;
    if (durA !== null && durB !== null && durA > 0) {
      durationDeltaPct = Math.round(((durB - durA) / durA) * 100);
      if (Math.abs(durationDeltaPct) >= 100) flipped.push('duration');
    }
    if (flipped.length > 0 || !sa || !sb) {
      stepDiffs.push({
        nodeId,
        name: sa?.name ?? sb?.name ?? null,
        statusA: sa?.status ?? null,
        statusB: sb?.status ?? null,
        durationA: durA,
        durationB: durB,
        durationDeltaPct,
        cacheA: sa?.cacheHitSource ?? null,
        cacheB: sb?.cacheHitSource ?? null,
        flipped: !sa ? ['only-in-B'] : !sb ? ['only-in-A'] : flipped,
      });
    }
  }

  // Thought delta at diverging nodes
  const thoughtsByStep = (bundle: RunBundle) => {
    const m = new Map<string, ThoughtLite[]>();
    for (const t of bundle.thoughts) {
      const key = t.stepId ?? '';
      const arr = m.get(key) ?? [];
      arr.push(t);
      m.set(key, arr);
    }
    return m;
  };
  const aThoughts = thoughtsByStep(a);
  const bThoughts = thoughtsByStep(b);
  const thoughtDeltas = stepDiffs.map((d) => {
    const sa = aMap.get(d.nodeId);
    const sb = bMap.get(d.nodeId);
    return {
      nodeId: d.nodeId,
      thoughtsA: sa ? (aThoughts.get(sa.id)?.map((t) => t.content) ?? []) : [],
      thoughtsB: sb ? (bThoughts.get(sb.id)?.map((t) => t.content) ?? []) : [],
    };
  });

  const result = {
    runA,
    runB,
    workflowId,
    cacheHitRateDelta: b.overview.cache.hitRate - a.overview.cache.hitRate,
    statusA: a.overview.run.status,
    statusB: b.overview.run.status,
    durationA: a.overview.run.durationMs,
    durationB: b.overview.run.durationMs,
    stepDiffs,
    thoughtDeltas,
  };

  const outDir = getArgValue(argv, '--out');
  if (outDir) {
    await fs.mkdir(outDir, { recursive: true });
    await fs.writeFile(path.join(outDir, 'compare.json'), JSON.stringify(result, null, 2));
    for (const [label, runId] of [
      ['runA', runA],
      ['runB', runB],
    ] as const) {
      const sub = path.join(outDir, label);
      await fs.mkdir(sub, { recursive: true });
      await tryWriteSnapshot(apiUrl, token, workflowId, runId, path.join(sub, 'snapshot.json'));
      await tryWriteTrace(apiUrl, token, workflowId, runId, path.join(sub, 'trace.zip'));
    }
    if (!hasFlag(argv, '--json')) console.log(`Wrote compare bundle to ${outDir}`);
  }

  if (hasFlag(argv, '--json') || !outDir) {
    console.log(JSON.stringify(result, null, 2));
  }
}

async function tryWriteTrace(
  apiUrl: string,
  token: string,
  workflowId: string,
  runId: string,
  outPath: string
): Promise<void> {
  const res = (await fetchDebug(apiUrl, token, workflowId, runId, '/trace')) as Envelope<{
    signedUrl: string;
  }>;
  if (!res.ok) return;
  const dl = await fetch(res.data.signedUrl);
  if (!dl.ok) return;
  await fs.writeFile(outPath, Buffer.from(await dl.arrayBuffer()));
}

async function tryWriteSnapshot(
  apiUrl: string,
  token: string,
  workflowId: string,
  runId: string,
  outPath: string
): Promise<void> {
  const run = await fetchRunDetail(apiUrl, token, workflowId, runId);
  if (!run) return;
  const snapPath = run.draftSnapshot
    ? `/workflows/${encodeURIComponent(workflowId)}/draft-snapshots/${encodeURIComponent(run.draftSnapshot.version)}`
    : run.snapshotVersion !== null && run.snapshotVersion !== undefined
      ? `/workflows/${encodeURIComponent(workflowId)}/snapshots/${run.snapshotVersion}`
      : null;
  if (!snapPath) return;
  const snap = await apiRequest<{ ok: boolean; snapshot?: { snapshotJson: unknown } }>(
    apiUrl,
    token,
    'GET',
    snapPath
  );
  if (!snap.snapshot) return;
  await fs.writeFile(outPath, JSON.stringify(snap.snapshot.snapshotJson, null, 2));
}

/**
 * `bundle` — pull every artifact for a run into one directory.
 *
 * Writes overview/steps/cache/thoughts/variables JSON, snapshot JSON, and
 * trace.zip. Logs/sentry-trace are best-effort (skipped if SENTRY_API_TOKEN
 * is not configured on the API). Produces a `bundle.json` manifest. Designed
 * for offline triage and ticket attachments.
 */
async function handleBundle(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const outDir = getArgValue(argv, '--out');
  if (!outDir) {
    console.error('Error: --out <dir> is required');
    process.exit(1);
  }
  await fs.mkdir(outDir, { recursive: true });

  const manifest: Array<{ file: string; source: string; ok: boolean; error?: string }> = [];

  const writeJson = async (file: string, source: string, fetcher: () => Promise<unknown>) => {
    try {
      const data = await fetcher();
      await fs.writeFile(path.join(outDir, file), JSON.stringify(data, null, 2));
      manifest.push({ file, source, ok: true });
    } catch (err) {
      manifest.push({ file, source, ok: false, error: String(err) });
    }
  };

  await writeJson('overview.json', `GET /workflows/${workflowId}/runs/${runId}/debug`, async () => {
    const r = (await fetchDebug(apiUrl, token, workflowId, runId)) as Envelope<unknown>;
    if (!r.ok) throw new Error(r.error ?? 'failed');
    return r.data;
  });
  await writeJson('steps.json', `/debug/steps`, async () => {
    const r = (await fetchDebug(apiUrl, token, workflowId, runId, '/steps')) as Envelope<unknown>;
    if (!r.ok) throw new Error(r.error ?? 'failed');
    return r.data;
  });
  await writeJson('thoughts.json', `/debug/thoughts`, async () => {
    const r = (await fetchDebug(
      apiUrl,
      token,
      workflowId,
      runId,
      '/thoughts'
    )) as Envelope<unknown>;
    if (!r.ok) throw new Error(r.error ?? 'failed');
    return r.data;
  });
  await writeJson('cache-debug.json', `/cache-debug`, async () => {
    const r = await apiRequest<{ ok: boolean; error?: string }>(
      apiUrl,
      token,
      'GET',
      `/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(runId)}/cache-debug`
    );
    if (r.ok === false) throw new Error(r.error ?? 'failed');
    return r;
  });
  await writeJson('logs.json', `/debug/logs`, async () => {
    const r = (await fetchDebug(apiUrl, token, workflowId, runId, '/logs')) as Envelope<unknown>;
    if (!r.ok) throw new Error(r.error ?? 'failed');
    return r.data;
  });
  await writeJson('sentry-trace.json', `/debug/sentry-trace`, async () => {
    const r = (await fetchDebug(
      apiUrl,
      token,
      workflowId,
      runId,
      '/sentry-trace'
    )) as Envelope<unknown>;
    if (!r.ok) throw new Error(r.error ?? 'failed');
    return r.data;
  });
  await writeJson('variables.json', `/debug/variables`, async () => {
    const r = (await fetchDebug(
      apiUrl,
      token,
      workflowId,
      runId,
      '/variables'
    )) as Envelope<unknown>;
    if (!r.ok) throw new Error(r.error ?? 'failed');
    return r.data;
  });

  // Snapshot JSON
  try {
    await tryWriteSnapshot(apiUrl, token, workflowId, runId, path.join(outDir, 'snapshot.json'));
    manifest.push({ file: 'snapshot.json', source: 'snapshots/:version', ok: true });
  } catch (err) {
    manifest.push({
      file: 'snapshot.json',
      source: 'snapshots/:version',
      ok: false,
      error: String(err),
    });
  }

  // trace.zip
  try {
    await tryWriteTrace(apiUrl, token, workflowId, runId, path.join(outDir, 'trace.zip'));
    manifest.push({ file: 'trace.zip', source: '/debug/trace', ok: true });
  } catch (err) {
    manifest.push({ file: 'trace.zip', source: '/debug/trace', ok: false, error: String(err) });
  }

  await fs.writeFile(
    path.join(outDir, 'bundle.json'),
    JSON.stringify({ runId, workflowId, manifest }, null, 2)
  );
  console.log(`Wrote bundle to ${outDir}`);
  for (const m of manifest) {
    console.log(`  ${m.ok ? '✓' : '✗'} ${m.file}${m.ok ? '' : `  (${m.error ?? 'failed'})`}`);
  }
}

/**
 * `materialize` — produce the canonical diagnostic workspace on disk.
 *
 * The same workspace shape powers the v2 failure-diagnostic agent and the
 * Claude Code `debug-run` skill. Pipeline:
 *   1. CLI hits `/debug/materialize-data` → receives `RunData` JSON + a signed
 *      S3 URL for trace.zip.
 *   2. CLI downloads trace.zip via the signed URL.
 *   3. CLI runs `assembleWorkspace` from `@chatsdet/diagnostic-workspace` locally.
 *      This is the same code the server runs in-process for the v2 agent —
 *      no drift possible.
 *
 * Trace reconstruction (Playwright headless) runs on the customer's machine,
 * not the server. After this command completes, start with `summary.md`.
 */
type MaterializeDataResponse =
  | {
      ok: true;
      data: {
        runData: RunData;
        signedTraceUrl: string | null;
        signedSandboxLogsUrl?: string | null;
      };
    }
  | { ok: false; error?: string };

export async function materializeRunWorkspace(opts: {
  runId: string;
  workflowId?: string;
  outDir: string;
  apiUrl: string;
  token: string;
  onWarning?: (message: string) => void;
}): Promise<{ workspace: string; runId: string; workflowId: string; runData: RunData }> {
  const workflowId = await resolveWorkflowIdForRun({
    runId: opts.runId,
    explicitWorkflowId: opts.workflowId,
    apiUrl: opts.apiUrl,
    token: opts.token,
  });
  const { assembleWorkspace } = await import('@chatsdet/diagnostic-workspace');

  const dataUrl = `${opts.apiUrl}/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(opts.runId)}/debug/materialize-data`;
  const dataRes = await fetch(dataUrl, { headers: { Authorization: `Bearer ${opts.token}` } });
  if (!dataRes.ok) {
    const errText = await dataRes.text().catch(() => '');
    throw new Error(`Failed to fetch run data: HTTP ${dataRes.status} ${errText}`);
  }
  const envelope = (await dataRes.json()) as MaterializeDataResponse;
  if (!envelope.ok) {
    throw new Error(`Failed: ${envelope.error ?? 'unknown error'}`);
  }
  const { runData, signedTraceUrl, signedSandboxLogsUrl } = envelope.data;

  await fs.mkdir(opts.outDir, { recursive: true });

  // Download trace.zip locally so the assembler can copy + reconstruct it.
  // Stream the body to disk — trace.zip can be 200MB+ and customer CI runners
  // are commonly memory-constrained.
  let traceZipPath: string | null = null;
  if (signedTraceUrl) {
    traceZipPath = path.join(opts.outDir, '.trace-source.zip');
    const traceRes = await fetch(signedTraceUrl);
    if (!traceRes.ok || !traceRes.body) {
      opts.onWarning?.(
        `Trace download failed (HTTP ${traceRes.status}); workspace will be marked partial.`
      );
      traceZipPath = null;
    } else {
      const handle = await fs.open(traceZipPath, 'w');
      try {
        const reader = traceRes.body.getReader();
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value) await handle.write(value);
        }
      } finally {
        await handle.close();
      }
    }
  }

  let sandboxLogsGzipPath: string | null = null;
  if (signedSandboxLogsUrl) {
    sandboxLogsGzipPath = path.join(opts.outDir, '.sandbox-logs-source.jsonl.gz');
    const logsRes = await fetch(signedSandboxLogsUrl);
    if (!logsRes.ok) {
      opts.onWarning?.(
        `Sandbox log download failed (HTTP ${logsRes.status}); workspace will be marked partial.`
      );
      sandboxLogsGzipPath = null;
    } else {
      await fs.writeFile(sandboxLogsGzipPath, Buffer.from(await logsRes.arrayBuffer()));
    }
  }

  await assembleWorkspace({
    runData,
    workspaceRoot: opts.outDir,
    traceZipPath,
    sandboxLogsGzipPath,
    cliCommand: command,
  });

  if (traceZipPath) {
    await fs.rm(traceZipPath, { force: true }).catch(() => undefined);
  }
  if (sandboxLogsGzipPath) {
    await fs.rm(sandboxLogsGzipPath, { force: true }).catch(() => undefined);
  }

  return { workspace: opts.outDir, runId: opts.runId, workflowId, runData };
}

async function handleSandboxLogs(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const dataUrl = `${apiUrl}/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(runId)}/debug/materialize-data`;
  const response = await fetch(dataUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    console.error(`Failed to fetch sandbox logs: HTTP ${response.status}`);
    process.exit(1);
  }
  const envelope = (await response.json()) as MaterializeDataResponse;
  if (!envelope.ok || !envelope.data.signedSandboxLogsUrl) {
    const metadata = envelope.ok ? envelope.data.runData.sandboxLogs : null;
    console.error(
      metadata?.reason ?? `Sandbox logs are ${metadata?.status ?? 'not available'} for this run.`
    );
    process.exit(1);
  }

  const artifact = await fetch(envelope.data.signedSandboxLogsUrl);
  if (!artifact.ok) {
    console.error(`Failed to download sandbox logs: HTTP ${artifact.status}`);
    process.exit(1);
  }
  const jsonl = gunzipSync(Buffer.from(await artifact.arrayBuffer()));
  const outPath = getArgValue(argv, '--out');
  if (outPath) {
    await fs.mkdir(path.dirname(outPath), { recursive: true });
    await fs.writeFile(outPath, jsonl);
  }
  if (hasFlag(argv, '--json')) {
    console.log(
      JSON.stringify(
        {
          runId,
          workflowId,
          out: outPath ?? null,
          metadata: envelope.data.runData.sandboxLogs ?? null,
          ...(outPath ? {} : { lines: jsonl.toString('utf8').trimEnd().split('\n') }),
        },
        null,
        2
      )
    );
    return;
  }
  if (outPath) {
    console.log(`Wrote retained sandbox logs to ${outPath}`);
  } else {
    process.stdout.write(jsonl);
  }
}

async function handleMaterialize(argv: string[], apiUrl: string, token: string): Promise<void> {
  const runId = requireRunId(argv);
  const workflowId = await resolveWorkflowId(argv, runId, apiUrl, token);
  const outDir = getArgValue(argv, '--out') ?? `./diag-${runId}`;

  try {
    await materializeRunWorkspace({
      runId,
      workflowId,
      outDir,
      apiUrl,
      token,
      onWarning: (message) => console.warn(message),
    });
  } catch (error) {
    console.error(error instanceof Error ? error.message : String(error));
    process.exit(1);
  }

  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify({ workspace: outDir, runId, workflowId }, null, 2));
    return;
  }
  console.log(`Materialized workspace at ${outDir}`);
  console.log(`Start with: cat ${path.join(outDir, 'summary.md')}`);
}
